<template>
    <div class="w-full max-w-sm flex justify-between items-start" :class="{'flex-col' : column}">
        
        <!-- Title -->
        <h4 v-if="title" class="font-bold mb-2 text-xs leading-tight w-full">
            {{ title }}
        </h4>
        <div class="min-w-2/3 w-full">
        <!-- BAR -->
        <div v-if="isGradient" class="relative h-1 overflow-hidden bg-gray-200">
            <div v-if="valueValid" class="absolute inset-0 pointer-events-none" :style="gradientStyle" />
        </div>

        <div v-else class="flex items-center gap-1">
            <div v-for="i in 5" :key="i" class="h-1 flex-1 transition-colors duration-200"
                :class="i === coercedValue ? 'bg-black' : 'bg-gray-200'" :aria-label="segmentAriaLabel(i)"
                role="presentation" />
        </div>

        <!-- Labels -->
        <div class="mt-2 flex justify-between text-[11px] leading-tight text-neutral-800">
            <span>{{ from }}</span>
            <span>{{ to }}</span>
        </div>

    </div>
    </div>
</template>

<script setup lang="ts">
/**
 * WorkbookGague (5‑stegs mätare)
 *
 * Props:
 *  - title (string)   Rubrik ovanför
 *  - from  (string)   Vänster etikett (t.ex. "Cold")
 *  - to    (string)   Höger etikett (t.ex. "Hot")
 *  - value (number|string|undefined)  1–5, eller tomt/'' för inget värde
 *  - type  ('gradient' | 'step')  default: 'gradient'
 *
 * Användning:
 * <WorkbookGague title="Temperature" from="Cold" to="Hot" value="2" />
 * <WorkbookGague title="Wind Protection" from="Min" to="Max" value="2" type="gradient" />
 * <WorkbookGague title="Insulation" from="Light" to="Heavy" value="2" type="step" />
 * <WorkbookGague title="Waterproofness" from="Light rain" to="Storm" value="" type="step" />
 * <WorkbookGague title="Breathability" from="Low" to="High" value="4" type="step" />
 */

import { computed } from 'vue'

const props = withDefaults(defineProps<{
    title?: string
    from?: string
    to?: string
    value?: number | string | null
    type?: 'gradient' | 'step'
    column?: boolean
}>(), {
    from: '',
    to: '',
    value: '',
    type: 'gradient',
    column: false,
})

/**
 * Normalisera value -> 1..5 eller NaN
 */
const coercedValue = computed(() => {
    const n = Number(props.value)
    return Number.isFinite(n) && n >= 1 && n <= 5 ? Math.round(n) : NaN
})

const valueValid = computed(() => !Number.isNaN(coercedValue.value))
const isGradient = computed(() => props.type === 'gradient')

/**
 * Gradient:
 *  Vi placerar en "spot" (radial-gradient) längs 0–100% baserat på (value-1)/4.
 *  Radialen är bred nog för att ge mjuk fade (justera --spot-size om du vill).
 */

const gradientStyle = computed(() => {
    if (!valueValid.value) return {}

    const pct = ((coercedValue.value - 1) / 4) * 100

    // Justerbara parametrar
    const coreWidth = 15   // svart kärna (%)
    const fadeWidth = 20  // fade på var sida (%)

    const startFade = pct - fadeWidth
    const startCore = pct - coreWidth / 2
    const endCore = pct + coreWidth / 2
    const endFade = pct + fadeWidth

    return {
        background: `linear-gradient(
      90deg,
      rgba(255,255,255,1) ${startFade}%,
      rgba(0,0,0,1) ${startCore}%,
      rgba(0,0,0,1) ${endCore}%,
      rgba(255,255,255,1) ${endFade}%
    )`,
        mixBlendMode: 'multiply',
    }
})

function segmentAriaLabel(i: number) {
    return i === coercedValue.value
        ? `Selected level ${i}`
        : `Level ${i}`
}
</script>

<style scoped>
/* (Valfri ytterligare styling kan läggas här) */
</style>